import React, { useState, useEffect } from 'react';
import { Typography, Grid, Divider, Box } from '@mui/material';

import { ShsSelectInput, ShsTimeInput } from 'src/components/common';
import { CenterFormFields } from 'src/constants/centers';
import { EducationLevelLabel } from 'src/constants/centers/form';
import { getDurationList } from 'src/utils/centers';

interface SchoolLesson {
    id: string;
    start: { label: string; source: string };
    end: { source: string };
}

interface CenterLessonsDurationProps {
    educationalLevel: string;
    lessonDurationSource: string;
    schoolLessons: (days: CenterFormFields) => SchoolLesson[];
    isTitleShown?: boolean;
}

export const CenterLessonsDuration: React.FC<CenterLessonsDurationProps> = ({
    educationalLevel,
    lessonDurationSource,
    schoolLessons,
    isTitleShown
}) => {
    const durationList = getDurationList(educationalLevel as EducationLevelLabel);

    const [weekdaySources, setWeekdaySources] = useState<string[]>([]);

    useEffect(() => {
        const weekday = schoolLessons(CenterFormFields.WEEKDAYS)
            .map((l) => [l.start.source, l.end.source])
            .flat();

        setWeekdaySources(weekday);
    }, [schoolLessons]);

    const validateNoOverlap = (currentSource: string) => (_value: any, allValues: any) => {
        const relevantSources = weekdaySources.includes(currentSource) ? weekdaySources : [];

        const intervals: {
            start: number;
            end: number;
            sourceStart: string;
            sourceEnd: string;
        }[] = [];

        const incorrectInvervals = new Set<string>();

        for (let i = 0; i < relevantSources.length; i += 2) {
            const startPath = relevantSources[i];
            const endPath = relevantSources[i + 1];
            const startVal = getValueFromPath(allValues, startPath);
            const endVal = getValueFromPath(allValues, endPath);

            if (startVal && endVal) {
                const startMinutes = parseTimeToMinutes(startVal);
                const endMinutes = parseTimeToMinutes(endVal);

                if (startMinutes < endMinutes) {
                    intervals.push({
                        start: startMinutes,
                        end: endMinutes,
                        sourceStart: startPath,
                        sourceEnd: endPath
                    });
                } else {
                    incorrectInvervals.add(startPath);
                    incorrectInvervals.add(endPath);
                }
            }
        }

        if (incorrectInvervals.has(currentSource)) {
            return 'Час кінця уроку повинен бути більшим за початок уроку';
        }

        const overlappedSources = new Set<string>();

        for (let i = 0; i < intervals.length; i++) {
            for (let j = i + 1; j < intervals.length; j++) {
                const a = intervals[i];
                const b = intervals[j];
                if (intervalsOverlap(a.start, a.end, b.start, b.end)) {
                    overlappedSources.add(a.sourceStart);
                    overlappedSources.add(a.sourceEnd);
                    overlappedSources.add(b.sourceStart);
                    overlappedSources.add(b.sourceEnd);
                }
            }
        }

        return overlappedSources.has(currentSource) ? 'Час уроку перетинається з іншим уроком' : undefined;
    };

    function parseTimeToMinutes(time: string): number {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    }

    function intervalsOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
        return aStart < bEnd && bStart < aEnd;
    }

    const getValueFromPath = (obj: any, path: string): string | null => {
        return path.split('.').reduce((acc, key) => acc?.[key], obj) ?? null;
    };

    return (
        <>
            {isTitleShown && (
                <Typography variant="h6" component="h6" mb={3} fontWeight="600">
                    Тривалість уроків
                </Typography>
            )}
            <Typography variant="h6" component="h6" lineHeight="1.5rem" mb={3} fontWeight="500">
                {educationalLevel}
            </Typography>
            <Grid container columnSpacing={4} rowSpacing={1}>
                <Grid item xs={4}>
                    <ShsSelectInput
                        source={lessonDurationSource}
                        label="Тривалість уроків (хв)"
                        choices={durationList}
                        required
                    />
                </Grid>
            </Grid>
            <Grid container sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Grid sx={{ display: 'flex', flexDirection: 'column' }}>
                    {schoolLessons(CenterFormFields.WEEKDAYS).map(({ id, start, end }) => (
                        <Grid
                            container
                            key={id}
                            sx={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '5px' }}>
                            <Box component="span" sx={{ minWidth: '90px' }}>
                                {start.label}
                            </Box>
                            <ShsTimeInput source={start.source} customValidate={validateNoOverlap(start.source)} />
                            <Divider
                                sx={{
                                    width: '7px',
                                    borderColor: 'black',
                                    borderWidth: '1px',
                                    alignSelf: 'center',
                                    marginTop: '8px'
                                }}
                            />
                            <ShsTimeInput source={end.source} customValidate={validateNoOverlap(start.source)} />
                        </Grid>
                    ))}
                </Grid>
            </Grid>
            <Divider
                sx={{
                    borderColor: '#D9D9D9',
                    borderWidth: '1px',
                    width: '100%',
                    alignSelf: 'center',
                    m: '10px'
                }}
            />
        </>
    );
};
